import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AutomatedAffiliateHeader } from '../components/AutomatedAffiliateHeader';
import { Footer } from '../components/Footer';
import { PricingSection } from '../components/PricingSection';
import { CheckCircleIcon, ChevronDownIcon, XIcon, CheckIcon, LoaderIcon } from 'lucide-react';
import { getBaseUrl } from '../utils/api';
export function AutomatedAffiliateMonetizationPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [cognitoId, setCognitoId] = useState<string | null>(null);
  const [userActivities, setUserActivities] = useState<string[]>([]);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [hasSignedUpViaEmail, setHasSignedUpViaEmail] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailDialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const emailSignupStatus = localStorage.getItem('ss_beta_join_email');
    if (emailSignupStatus === 'true') {
      setHasSignedUpViaEmail(true);
    }
  }, []);
  const fetchUserData = useCallback(async (id: string) => {
    setIsLoadingUser(true);
    try {
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}user?cognitoId=${id}`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.user) {
          setUserName(data.user.name);
          setUserActivities(data.user.activity || []);
        }
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
    } finally {
      setIsLoadingUser(false);
    }
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      setCognitoId(id);
      fetchUserData(id);
    }
  }, [fetchUserData]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emailDialogRef.current && !emailDialogRef.current.contains(event.target as Node)) {
        setShowEmailDialog(false);
      }
    };
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowEmailDialog(false);
      }
    };
    if (showEmailDialog) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscKey);
        document.body.style.overflow = 'auto';
      };
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'auto';
    };
  }, [showEmailDialog]);
  useEffect(() => {
    if (!showEmailDialog) {
      setEmailInput('');
    }
  }, [showEmailDialog]);
  const handleJoinBetaClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (cognitoId && userName) {
      setIsSubmitting(true);
      try {
        const baseUrl = getBaseUrl();
        const response = await fetch(`${baseUrl}user/activity`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            activity_name: 'ss_beta_join',
            cognito_id: cognitoId
          })
        });
        if (response.ok) {
          if (cognitoId) {
            await fetchUserData(cognitoId);
          }
        } else {
          console.error('Failed to log activity');
        }
      } catch (error) {
        console.error('Error logging activity:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setShowEmailDialog(true);
    }
  };
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput) return;
    setIsSubmitting(true);
    try {
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}user/activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          activity_name: 'ss_beta_join',
          cognito_id: '1',
          activity_data: {
            email: emailInput
          }
        })
      });
      if (response.ok) {
        localStorage.setItem('ss_beta_join_email', 'true');
        setHasSignedUpViaEmail(true);
        setShowEmailDialog(false);
        setEmailInput('');
      } else {
        console.error('Failed to submit email');
      }
    } catch (error) {
      console.error('Error submitting email:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const firstName = userName ? userName.split(' ')[0] : null;
  const isSignedUpForBeta = userActivities.includes('ss_beta_join') || hasSignedUpViaEmail;
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    question: 'Will this make my emails feel spammy?',
    answer: 'No. We only insert offers that match your content and audience. The AI ensures relevance, and you can review or remove any placement before sending.'
  }, {
    question: 'Can I approve or edit the offers before sending?',
    answer: 'Absolutely. You have full control. Review, edit, or remove any offer. Or let it run automatically if you trust the system.'
  }, {
    question: 'What if I already use some affiliate programs?',
    answer: 'Perfect! We work alongside your existing Amazon program. You can prioritize certain offers or let us fill gaps automatically.'
  }, {
    question: 'How hard is it to set up?',
    answer: "5 minutes. Connect your Kit account, add our block into your email draft, and you're done. No technical skills required."
  }, {
    question: 'Do I have to change how I write my newsletter?',
    answer: 'Not at all. Write exactly as you do now. Our AI adapts to your style and content.'
  }, {
    question: 'Does this only work on Kit.com?',
    answer: "Yes, we're built specifically for Kit.com to ensure seamless integration and the best experience."
  }];
  return <div className="flex flex-col min-h-screen bg-white font-sans" data-id="element-966">
    <AutomatedAffiliateHeader data-id="element-967" />
    {isLoadingUser ? <main className="flex-grow pt-24 flex items-center justify-center min-h-[60vh]" data-id="element-968">
      <div className="text-center" data-id="element-969">
        <LoaderIcon className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" data-id="element-970" />
        <p className="text-gray-600 text-lg" data-id="element-971">Loading...</p>
      </div>
    </main> : <main className="flex-grow pt-24" data-id="element-972">
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-973">
        <div className="max-w-4xl mx-auto text-center" data-id="element-974">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full mb-8" data-id="element-975">
            <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" data-id="element-976"></span>
            <span className="text-sm font-medium text-blue-700" data-id="element-977">
              Newsfiliate Pro for Kit.com Creators
            </span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-978">
            Intelligent affiliate monetization for your Kit newsletters&nbsp;
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600" data-id="element-979">
              zero extra work
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-3xl mx-auto" data-id="element-980">
            Transform your newsletter into an AI-driven monetization engine
            that uses your own data to automatically test, refine, and scale
            affiliate revenue.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6" data-id="element-981">
            {isSignedUpForBeta ? <div className="w-full sm:w-auto bg-green-50 border-2 border-green-200 rounded-xl p-6" data-id="element-982">
              {firstName && <p className="text-lg font-semibold text-gray-900 text-center mb-1" data-id="element-983">
                🎉 {firstName}, you're signed up for the beta! 🎉
              </p>}
              {!firstName && <p className="text-lg font-semibold text-gray-900 text-center mb-2" data-id="element-984">
                  🎉 You're signed up for the beta! 🎉
                </p>}
              <p className="text-gray-700 text-center" data-id="element-985">
                You will hear back from us soon.
              </p>
            </div> : <div className="flex flex-col items-center w-full sm:w-auto" data-id="element-986">
              {firstName && <p className="text-lg text-gray-700 mb-4 text-center" data-id="element-987">
                {firstName}, ready to join the beta?
              </p>}
              <button onClick={handleJoinBetaClick} disabled={isSubmitting} className="w-full sm:w-auto px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-988">
                Join the Beta
              </button>
            </div>}
          </div>
          <p className="text-sm text-gray-500" data-id="element-989">
            For Kit.com newsletter creators who want to monetize without
            becoming ad managers
          </p>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-id="element-990">
        <div className="max-w-4xl mx-auto" data-id="element-991">
          <div className="text-center mb-12" data-id="element-992">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-993">
              You're leaving money on the table because monetization feels
              like a second job
            </h2>
            <p className="text-xl text-gray-600" data-id="element-994">Right now, you probably:</p>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-gray-100" data-id="element-995">
            <ul className="space-y-6" data-id="element-996">
              <li className="flex items-start" data-id="element-997">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mt-1" data-id="element-998">
                  <XIcon className="w-5 h-5 text-red-600" data-id="element-999" />
                </div>
                <div className="ml-4" data-id="element-1000">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-1001">
                    Jump between writing and hunting
                  </h3>
                  <p className="text-gray-600" data-id="element-1002">
                    Wasting hours searching for affiliate products instead of
                    creating content.
                  </p>
                </div>
              </li>
              <li className="flex items-start" data-id="element-1003">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mt-1" data-id="element-1004">
                  <XIcon className="w-5 h-5 text-red-600" data-id="element-1005" />
                </div>
                <div className="ml-4" data-id="element-1006">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-1007">
                    Guessing games
                  </h3>
                  <p className="text-gray-600" data-id="element-1008">
                    Trying to predict which products or offers fit your
                    audience and each issue without data.
                  </p>
                </div>
              </li>
              <li className="flex items-start" data-id="element-1009">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mt-1" data-id="element-1010">
                  <XIcon className="w-5 h-5 text-red-600" data-id="element-1011" />
                </div>
                <div className="ml-4" data-id="element-1012">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-1013">
                    Broken flow
                  </h3>
                  <p className="text-gray-600" data-id="element-1014">
                    Copy/pasting links and boxes, interrupting your creative
                    process.
                  </p>
                </div>
              </li>
              <li className="flex items-start" data-id="element-1015">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mt-1" data-id="element-1016">
                  <XIcon className="w-5 h-5 text-red-600" data-id="element-1017" />
                </div>
                <div className="ml-4" data-id="element-1018">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-1019">
                    Relevance anxiety
                  </h3>
                  <p className="text-gray-600" data-id="element-1020">
                    Worrying if offers are too pushy, off-topic, or annoying
                    to readers.
                  </p>
                </div>
              </li>
            </ul>
            <div className="mt-10 pt-8 border-t border-gray-100 text-center" data-id="element-1021">
              <p className="text-lg font-medium text-gray-900" data-id="element-1022">
                Result: Your list has value, but manual monetization steals
                the time and focus you need for creating great content.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-1023">
        <div className="max-w-6xl mx-auto" data-id="element-1024">
          <div className="text-center mb-16" data-id="element-1025">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1026">
              How it works: Set it once, earn forever
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1027">
              No extra tabs. No constant tweaking. No manual link hunting.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-1028">
            <div className="text-center" data-id="element-1029">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-blue-600" data-id="element-1030">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1031">
                Connect your Kit newsletter
              </h3>
              <p className="text-gray-600" data-id="element-1032">
                Add our block into your email draft. That's the only setup
                required.
              </p>
            </div>
            <div className="text-center" data-id="element-1033">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-purple-600" data-id="element-1034">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1035">
                We find the perfect match
              </h3>
              <p className="text-gray-600" data-id="element-1036">
                AI scans your content and audience to select the best-fitting
                Amazon affiliate offers for that specific issue.
              </p>
            </div>
            <div className="text-center" data-id="element-1037">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-green-600" data-id="element-1038">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1039">
                Automatic insertion
              </h3>
              <p className="text-gray-600" data-id="element-1040">
                A clean, contextual affiliate offer box appears in each issue
                automatically. You earn while you sleep.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Intelligence Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white" data-id="element-1041">
        <div className="max-w-6xl mx-auto" data-id="element-1042">
          <div className="text-center mb-16" data-id="element-1043">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1044">
              AI that learns your audience and gets smarter over time
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1045">
              This isn't just automation—it's intelligent monetization that
              understands your content, tracks what works, and discovers new
              opportunities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1046">
            {/* Value Prop A: Content & User Understanding */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-blue-100 hover:shadow-md transition-shadow" data-id="element-1047">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6" data-id="element-1048">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1049">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" data-id="element-1050" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1051">
                Deep Content Analysis
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-1052">
                Our AI reads every newsletter you send, understanding your
                topics, tone, and audience interests. It suggests products
                that genuinely match your content—not random offers that feel
                forced.
              </p>
            </div>

            {/* Value Prop B: Performance Optimization */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-purple-100 hover:shadow-md transition-shadow" data-id="element-1053">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6" data-id="element-1054">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1055">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" data-id="element-1056" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1057">
                Continuous Optimization
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-1058">
                We track which offers your audience clicks and converts on,
                then automatically adjust future recommendations. Your
                monetization gets better with every send—no manual analysis
                required.
              </p>
            </div>

            {/* Value Prop C: Trend Discovery */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-green-100 hover:shadow-md transition-shadow" data-id="element-1059">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6" data-id="element-1060">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1061">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" data-id="element-1062" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1063">
                Trend Intelligence
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-1064">
                Our system monitors trending products and emerging
                opportunities across the web, filtering for what's relevant to
                your specific audience. Stay ahead without endless research.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-1065">
            <p className="text-lg text-gray-700 font-medium" data-id="element-1066">
              The result? Offers that feel hand-picked for your
              readers—because they basically are.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-id="element-1067">
        <div className="max-w-6xl mx-auto" data-id="element-1068">
          <div className="text-center mb-16" data-id="element-1069">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900" data-id="element-1070">
              What you get with automated affiliate monetization
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1071">
            {['Monetize every issue without breaking your writing flow', 'Only show offers that make sense for your readers', "Turn 'I'll monetize later' into automatic, done-for-you revenue", 'Set it up once and let it work in the background', 'Stay in control: review, edit, or override any placement if you want', 'Maintain reader trust with relevant, contextual offers'].map((benefit, index) => <div key={index} className="flex items-start p-6 bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-1072">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 mr-4 flex-shrink-0 mt-0.5" data-id="element-1073" />
              <p className="text-gray-800 font-medium" data-id="element-1074">{benefit}</p>
            </div>)}
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-1075">
        <div className="max-w-5xl mx-auto" data-id="element-1076">
          <div className="text-center mb-16" data-id="element-1077">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900" data-id="element-1078">
              Who this is for (and who it's not for)
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1079">
            <div className="bg-blue-50 rounded-2xl p-8 border border-blue-100" data-id="element-1080">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1081">
                <span className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm mr-3" data-id="element-1082">
                  <CheckIcon className="w-5 h-5" data-id="element-1083" />
                </span>
                Perfect if you...
              </h3>
              <ul className="space-y-4" data-id="element-1084">
                <li className="flex items-start" data-id="element-1085">
                  <CheckIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" data-id="element-1086" />
                  <span className="text-gray-800" data-id="element-1087">
                    Run a newsletter on Kit.com
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1088">
                  <CheckIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" data-id="element-1089" />
                  <span className="text-gray-800" data-id="element-1090">
                    Want extra revenue but don't want to become an ad manager
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1091">
                  <CheckIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" data-id="element-1092" />
                  <span className="text-gray-800" data-id="element-1093">
                    Care about reader trust and relevance
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1094">
                  <CheckIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" data-id="element-1095" />
                  <span className="text-gray-800" data-id="element-1096">
                    Spend more time writing than monetizing
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200" data-id="element-1097">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1098">
                <span className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white text-sm mr-3" data-id="element-1099">
                  <XIcon className="w-5 h-5" data-id="element-1100" />
                </span>
                Not a fit if you...
              </h3>
              <ul className="space-y-4" data-id="element-1101">
                <li className="flex items-start" data-id="element-1102">
                  <XIcon className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-1103" />
                  <span className="text-gray-600" data-id="element-1104">
                    Want to manually control every individual ad and placement
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1105">
                  <XIcon className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-1106" />
                  <span className="text-gray-600" data-id="element-1107">
                    Don't use Kit for your newsletter
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1108">
                  <XIcon className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-1109" />
                  <span className="text-gray-600" data-id="element-1110">
                    Prefer traditional sponsorships only
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Privacy Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-1111">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1112">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-12" data-id="element-1113">
            Your readers' trust is our priority
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 text-left" data-id="element-1114">
            <div className="flex items-start" data-id="element-1115">
              <CheckCircleIcon className="w-6 h-6 text-green-500 mr-4 flex-shrink-0 mt-0.5" data-id="element-1116" />
              <p className="text-gray-800 text-lg" data-id="element-1117">
                No spammy, irrelevant offers. Only contextual matches
              </p>
            </div>
            <div className="flex items-start" data-id="element-1118">
              <CheckCircleIcon className="w-6 h-6 text-green-500 mr-4 flex-shrink-0 mt-0.5" data-id="element-1119" />
              <p className="text-gray-800 text-lg" data-id="element-1120">
                AI analyzes your content to ensure alignment with your
                audience
              </p>
            </div>
            <div className="flex items-start" data-id="element-1121">
              <CheckCircleIcon className="w-6 h-6 text-green-500 mr-4 flex-shrink-0 mt-0.5" data-id="element-1122" />
              <p className="text-gray-800 text-lg" data-id="element-1123">
                You always have final say. Review or remove any offer before
                sending
              </p>
            </div>
            <div className="flex items-start" data-id="element-1124">
              <CheckCircleIcon className="w-6 h-6 text-green-500 mr-4 flex-shrink-0 mt-0.5" data-id="element-1125" />
              <p className="text-gray-800 text-lg" data-id="element-1126">
                Pause anytime, no long-term commitment
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection variant="beta" data-id="element-1127" />

      {/* FAQ Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-id="element-1128">
        <div className="max-w-3xl mx-auto" data-id="element-1129">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center" data-id="element-1130">
            Frequently asked questions
          </h2>
          <div className="space-y-4" data-id="element-1131">
            {faqs.map((faq, index) => <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-1132">
              <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors focus:outline-none" data-id="element-1133">
                <span className="font-semibold text-gray-900 text-lg" data-id="element-1134">
                  {faq.question}
                </span>
                <ChevronDownIcon className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${openFaqIndex === index ? 'transform rotate-180' : ''}`} data-id="element-1135" />
              </button>
              {openFaqIndex === index && <div className="px-6 pb-6 pt-0" data-id="element-1136">
                <p className="text-gray-700 leading-relaxed" data-id="element-1137">
                  {faq.answer}
                </p>
              </div>}
            </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-1138">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1139">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1140">
            Ready to monetize without the manual work?
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto" data-id="element-1141">
            If you're a Kit newsletter creator who wants to earn more without
            becoming an ad manager, this is for you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6" data-id="element-1142">
            {isSignedUpForBeta ? <div className="w-full sm:w-auto bg-green-50 border-2 border-green-200 rounded-xl p-6" data-id="element-1143">
              {firstName && <p className="text-lg font-semibold text-gray-900 text-center mb-1" data-id="element-1144">
                {firstName}, you're signed up for the beta!
              </p>}
              <p className="text-lg font-semibold text-gray-900 text-center mb-2" data-id="element-1145">
                🎉 You're signed up for the beta! 🎉
              </p>
              <p className="text-gray-700 text-center" data-id="element-1146">
                You will hear back from us soon.
              </p>
            </div> : <div className="flex flex-col items-center w-full sm:w-auto" data-id="element-1147">
              {firstName && <p className="text-lg text-gray-700 mb-4 text-center" data-id="element-1148">
                {firstName}, ready to join the beta?
              </p>}
              <button onClick={handleJoinBetaClick} disabled={isSubmitting} className="w-full sm:w-auto px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1149">
                Join the Beta
              </button>
            </div>}
          </div>
          <p className="text-gray-500" data-id="element-1150">
            No commitment. Try it on your next issue.
          </p>
        </div>
      </section>
    </main>}

    {showEmailDialog && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1151">
        <div ref={emailDialogRef} className="bg-white rounded-xl shadow-lg max-w-md w-full overflow-hidden" data-id="element-1152">
          <div className="flex justify-end p-4" data-id="element-1153">
            <button onClick={() => setShowEmailDialog(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1154">
              <XIcon className="w-6 h-6" data-id="element-1155" />
            </button>
          </div>
          <div className="px-6 pb-6 pt-2" data-id="element-1156">
            <h2 className="text-2xl font-bold text-center text-gray-800 mb-2" data-id="element-1157">
              Join the Beta
            </h2>
            <p className="text-gray-600 text-center mb-6" data-id="element-1158">
              Enter your email to join the beta program
            </p>
            <form className="space-y-4" onSubmit={handleEmailSubmit} data-id="element-1159">
              <div data-id="element-1160">
                <input type="email" placeholder="Your email address" required value={emailInput} onChange={e => setEmailInput(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" data-id="element-1161" />
              </div>
              <button type="submit" className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg transition-all hover:shadow-md hover:bg-blue-700 flex items-center justify-center" disabled={isSubmitting} data-id="element-1162">
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          </div>
        </div>
      </div>}
    <Footer data-id="element-1163" />
  </div>;
}